// MaturaRecursiveSequenceTermClosedTask.js
// Zadanie maturalne zamkniete: oblicz wskazany wyraz ciagu z rekurencji liniowej.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function signedLatex(n) {
  if (n === 0) return "";
  return n > 0 ? `+${n}` : `-${Math.abs(n)}`;
}

function termLatex(k) {
  if (k === 1) return "a_n";
  if (k === -1) return "-a_n";
  return `${k}a_n`;
}

function factorLatex(n) {
  return n < 0 ? `(${n})` : String(n);
}

function ordinalText(n) {
  return ({ 3: "Trzeci", 4: "Czwarty", 5: "Piąty" })[n] || `${n}.`;
}

function buildValues(a1, k, b, targetIndex) {
  const values = [a1];
  for (let i = 2; i <= targetIndex; i += 1) {
    values.push(k * values[i - 2] + b);
  }
  return values;
}

function uniqueOptionValues(values, correctValue, k, b) {
  const candidates = [
    correctValue,
    values[1],
    correctValue + b,
    correctValue - b,
    correctValue + k,
    correctValue - k,
    k * values[0] - b,
    correctValue + 1,
    correctValue - 1,
  ];

  const out = [];
  for (const value of candidates) {
    if (Number.isInteger(value) && !out.includes(value)) out.push(value);
    if (out.length === 4) break;
  }

  let delta = 2;
  while (out.length < 4) {
    for (const value of [correctValue + delta, correctValue - delta]) {
      if (!out.includes(value)) out.push(value);
      if (out.length === 4) break;
    }
    delta += 1;
  }

  return out.slice(0, 4);
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaRecursiveSequenceTermClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: "14.1",
    },
    data: {
      a1: 2,
      k: 2,
      b: 1,
      targetIndex: 3,
      values: [2, 5, 11],
      correctValue: 11,
      recurrenceLatex: "a_{n+1}=2a_n+1",
      options: [
        { label: "A", value: 4, latex: "4", isCorrect: false },
        { label: "B", value: 5, latex: "5", isCorrect: false },
        { label: "C", value: 7, latex: "7", isCorrect: false },
        { label: "D", value: 11, latex: "11", isCorrect: true },
      ],
      correctIndex: 3,
      maxPoints: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.session === "2026_CZE") {
    const original = generationOptions.variant === "original";
    const a1 = original ? 2 : generationOptions.parameters?.a1 ?? pick([1, 2, 3, 4]);
    const b = original ? -6 : generationOptions.parameters?.b ?? pick([-3, -4, -5, -6]);
    const values = buildValues(a1, 1, b, 5), correctValue = values[4];
    const numbers = [values[1], values[3], values[4], a1 + 5 * b];
    return { type: "MaturaRecursiveSequenceTermClosedTask", source: { variant: original ? "original" : "random", exam: "MATURA", session: "2026_CZE", taskNumber: "14.1" },
      data: { a1, k: 1, b, targetIndex: 5, values, correctValue, recurrenceLatex: `a_{n+1}=a_n${signedLatex(b)}`,
        options: numbers.map((value, i) => ({ label: LETTERS[i], value, latex: String(value), isCorrect: value === correctValue })), correctIndex: 2, maxPoints: 1 } };
  }
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  let params = null;

  for (let attempt = 0; attempt < 100; attempt += 1) {
    const a1 = randInt(-4, 6);
    const k = pick([2, 3, 4]);
    const b = pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
    const targetIndex = pick([3, 4]);
    const values = buildValues(a1, k, b, targetIndex);
    const correctValue = values[targetIndex - 1];

    if (Math.abs(correctValue) > 90) continue;
    params = { a1, k, b, targetIndex, values, correctValue };
    break;
  }

  if (!params) {
    params = {
      a1: 2,
      k: 2,
      b: 1,
      targetIndex: 3,
      values: [2, 5, 11],
      correctValue: 11,
    };
  }

  const optionValues = shuffle(
    uniqueOptionValues(params.values, params.correctValue, params.k, params.b)
  );

  const options = optionValues.map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: String(value),
    isCorrect: value === params.correctValue,
  }));

  return {
    type: "MaturaRecursiveSequenceTermClosedTask",
    data: {
      ...params,
      recurrenceLatex: `a_{n+1}=${termLatex(params.k)}${signedLatex(params.b)}`,
      options,
      correctIndex: options.findIndex((opt) => opt.isCorrect),
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { a1, recurrenceLatex, targetIndex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Ciąg <InlineMath math={"(a_n)"} /> jest określony następująco:
      </p>

      <BlockMath
        math={`\\begin{cases}a_1=${a1}\\\\${recurrenceLatex}\\end{cases}`}
      />
      <p>Wzór rekurencyjny obowiązuje dla każdej liczby naturalnej <InlineMath math="n\geq1" />.</p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>

      <p>
        {ordinalText(targetIndex)} wyraz ciągu <InlineMath math={"(a_n)"} />{" "}
        jest równy:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={options.map((opt, idx) => ({
        value: idx,
        label: opt.label,
        textNode: opt.label,
      }))}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderExplanation({ data }) {
  const { a1, k, b, targetIndex, values, recurrenceLatex, correctValue } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z podanego wzoru rekurencyjnego:
      </p>
      <BlockMath math={`a_1=${a1},\\quad ${recurrenceLatex}`} />

      {values.slice(1).map((value, idx) => {
        const currentIndex = idx + 2;
        const prev = values[idx];
        return (
          <div key={currentIndex}>
            <p>
              <strong>Krok {currentIndex}.</strong> Obliczamy{" "}
              <InlineMath math={`a_{${currentIndex}}`} />:
            </p>
            <BlockMath
              math={`\\begin{aligned}a_{${currentIndex}}&=${k}\\cdot a_{${currentIndex - 1}}${signedLatex(
                b
              )}\\\\&=${k}\\cdot${factorLatex(prev)}${signedLatex(b)}=${value}\\end{aligned}`}
            />
          </div>
        );
      })}

      <p>
        Zatem <InlineMath math={`a_{${targetIndex}}=${correctValue}`} />.
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}
